/**
 * Commission page — typed wrappers around the authenticated endpoints
 * that power the configurator.
 *
 * Every async call accepts an AbortSignal so a stale request can be
 * cancelled when the buyer types past the debounce window or
 * navigates away. Each function returns either the typed payload or
 * throws an `ApiError` with a buyer-facing message — callers dispatch
 * the success path with the data, the error path with the message,
 * and the reducer holds the rest of the state.
 *
 * All endpoints are authenticated. The SDK's `client` singleton is
 * configured at app startup with `credentials: "include"` and an
 * Authorization-header interceptor that reads from the in-memory
 * access token — no manual header threading required here.
 *
 * The Custom Inquiry endpoint (`/api/v1/intake/inquiry`) is not yet
 * in the generated SDK; until the next `pnpm api:codegen` run, it's
 * called via raw fetch with the access token passed explicitly. The
 * type signature documents the dependency so the migration is
 * obvious when the SDK catches up.
 */

import {
  commissionEngagementApiV1ClientPortalCommissionPost,
  getCurrentAttestationApiV1AttestationCurrentGet,
  previewCartPricingApiV1ClientPortalPricingPreviewPost,
  runPreviewApiV1DiscoveryPreviewPost,
  getApiBaseUrl,
} from "@hackereyes/api-client";
import type { IntakeRequest } from "@hackereyes/api-client";

// --------------------------------------------------------------------------- //
// Typed payloads                                                              //
// --------------------------------------------------------------------------- //

export interface DiscoveredAsset {
  hostname: string;
  alive: boolean;
  http_status: number | null;
  ip: string | null;
  scheme_used: string | null;
  sources: string[];
}

export interface DiscoveryResponse {
  seed_domain: string;
  discovered_assets: DiscoveredAsset[];
  asset_count: number;
  scan_duration_ms: number;
}

export interface CartLineItem {
  label: string;
  detail: string;
  amount_cents: number;
  quantity: number;
}

export interface CartBreakdown {
  subtotal_cents: number;
  line_items: CartLineItem[];
  domain_count: number;
  subdomain_count: number;
  ip_count: number;
  free_subdomains_used: number;
  extra_subdomains: number;
  extra_domains: number;
  extra_ips: number;
  error?: string | null;
}

export interface AttestationCurrent {
  version: number;
  text_body: string;
  text_hash: string;
}

export interface CommissionResponse {
  checkout_url: string;
  session_id: string;
  amount_cents: number;
  pending_intake_id: string;
}

export interface InquiryResponse {
  lead_id: string;
  submitted_at: string;
}

// --------------------------------------------------------------------------- //
// Error class                                                                 //
// --------------------------------------------------------------------------- //

/** API-level error that carries a buyer-facing message. The reducer
 *  routes the message into the relevant section's error state for
 *  display. */
export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

interface FetchOptions {
  signal?: AbortSignal;
}

/** Adapter: pull the buyer-facing detail from a Hey API SDK error
 *  payload. Returns the FastAPI `detail` field when present, otherwise
 *  the SDK's stringified error or a generic fallback.
 *
 *  When the only thing we have is the HTTP status, surface it so the
 *  operator can correlate with server logs (`journalctl -u recon-api`)
 *  instead of staring at a blank "X failed" message. */
function detailOf(err: unknown, fallback: string): string {
  if (err && typeof err === "object") {
    const detail = (err as { detail?: unknown }).detail;
    if (typeof detail === "string" && detail.length > 0) return detail;
    const status = (err as { status?: unknown }).status;
    if (typeof status === "number" && status > 0) {
      return `${fallback} (HTTP ${status})`;
    }
  }
  return fallback;
}

// --------------------------------------------------------------------------- //
// Public API                                                                  //
// --------------------------------------------------------------------------- //

/** Run a passive discovery scan against the buyer's typed domain.
 *  Authenticated via portal cookie + bearer token. */
export async function runDiscovery(
  seedDomain: string,
  opts: FetchOptions = {},
): Promise<DiscoveryResponse> {
  const { data, error } = await runPreviewApiV1DiscoveryPreviewPost({
    body: { seed_domain: seedDomain },
    signal: opts.signal,
  });
  if (error) {
    throw new ApiError(0, detailOf(error, "Discovery scan failed."));
  }
  return data as DiscoveryResponse;
}

/** Compute the server-authoritative price for a draft cart.
 *
 *  The buyer never tells the server the price; the server reclassifies
 *  asset kinds and recomputes from the rate card. The `line_items`
 *  returned here render verbatim in the OrderSummary, so the SPA
 *  can't drift from what Stripe actually charges. */
export async function previewCartPricing(
  assets: Array<{ value: string; kind: "domain" | "subdomain" | "ip" }>,
  opts: FetchOptions = {},
): Promise<CartBreakdown> {
  const { data, error } = await previewCartPricingApiV1ClientPortalPricingPreviewPost({
    body: { assets },
    signal: opts.signal,
  });
  if (error) {
    throw new ApiError(0, detailOf(error, "Could not compute the price."));
  }
  return data as CartBreakdown;
}

/** Fetch the currently-published Authorization to Assess text +
 *  version + hash. The configurator renders the text verbatim; the
 *  hash is echoed back on submission so the server can refuse stale
 *  submissions that loaded the text before a version bump. */
export async function fetchCurrentAttestation(
  opts: FetchOptions = {},
): Promise<AttestationCurrent> {
  const { data, error } = await getCurrentAttestationApiV1AttestationCurrentGet({
    signal: opts.signal,
  });
  if (error) {
    throw new ApiError(0, detailOf(error, "Could not load the authorization text."));
  }
  return data as AttestationCurrent;
}

/** Submit the cart + attestation, bind the case to the authenticated
 *  client, mint a Stripe Checkout Session. The response carries the
 *  URL the page redirects to.
 *
 *  Authenticated: the server reads `client_id` from the bearer token,
 *  not from any body field. The buyer never has to type contact info
 *  that auth already provides. */
export async function commissionEngagement(
  payload: CommissionSubmission,
  opts: FetchOptions = {},
): Promise<CommissionResponse> {
  const { data, error } = await commissionEngagementApiV1ClientPortalCommissionPost({
    body: payload,
    signal: opts.signal,
  });
  if (error) {
    throw new ApiError(0, detailOf(error, "Could not start checkout. Please try again."));
  }
  return data as CommissionResponse;
}

/** Submit a Custom Inquiry. Lands in `intake_lead`; the operator
 *  follows up by email.
 *
 *  TODO(api-codegen): once the next `pnpm api:codegen` run picks up
 *  the inquiry endpoint, replace the raw fetch with
 *  `submitCustomInquiryApiV1IntakeInquiryPost`. Until then, we pass
 *  the access token explicitly so the request goes through with the
 *  bearer header the SDK interceptor would normally attach. */
export async function submitCustomInquiry(
  payload: CustomInquirySubmission,
  accessToken: string | null,
  opts: FetchOptions = {},
): Promise<InquiryResponse> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }
  let response: Response;
  try {
    response = await fetch(`${getApiBaseUrl()}/api/v1/intake/inquiry`, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(payload),
      signal: opts.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(0, "We couldn't reach the server. Check your connection and try again.");
  }
  if (response.ok) {
    return (await response.json()) as InquiryResponse;
  }
  let message = response.statusText || "Couldn't submit your inquiry.";
  try {
    const body = (await response.json()) as { detail?: string };
    if (body && typeof body.detail === "string" && body.detail.length > 0) {
      message = body.detail;
    }
  } catch {
    // body wasn't JSON
  }
  throw new ApiError(response.status, message);
}

// --------------------------------------------------------------------------- //
// Submission payload shapes                                                   //
// --------------------------------------------------------------------------- //

/** Shape posted to `/api/v1/client-portal/commission`. Re-exported
 *  from the generated SDK so the type stays in lock-step with the
 *  backend schema — when `pnpm api:codegen` updates the SDK, this
 *  type updates with it.
 *
 *  The backend binds the case to the authenticated `client_id` from
 *  the bearer token; the contact fields ride along but the server
 *  reads contact data from the client record, not from these. */
export type CommissionSubmission = IntakeRequest;

export interface CustomInquirySubmission {
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  company: string | null;
  scope_description: string;
  urgency: string | null;
  attempted_cart: Array<{ value: string; kind: string }>;
  source: string;
}
